import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDesignColors } from '../../hooks/useDesignColors';

interface ConversationLimitAlertProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'warning' | 'limit';
  remainingConversations?: number;
}

export function ConversationLimitAlert({ isOpen, onClose, type, remainingConversations }: ConversationLimitAlertProps) {
  const navigate = useNavigate();
  const { colors } = useDesignColors();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="max-w-md w-full rounded-xl p-6 relative"
        style={{
          background: `linear-gradient(135deg, ${colors.gradientStart}10, ${colors.gradientEnd}10)`,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-white mb-2">
              {type === 'warning' 
                ? 'Warning: Conversation limit approaching'
                : 'Conversation limit reached'
              }
            </h3>
            <p className="text-gray-300 mb-6">
              {type === 'warning' 
                ? `You only have left ${remainingConversations} conversations available this month. To continue enjoying your chatbot, we recommend upgrading to a higher plan.`
                : 'You have reached your chat limit for this month. To continue using your chatbot, please upgrade to a higher plan.'
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
              >
                Later
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
                className="flex-1 px-4 py-2 rounded-lg text-white transition-all"
                style={{
                  background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                }}
              >
                View pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}